import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  Building,
  ArrowRight,
  Shield,
  ShieldCheck,
  Clock,
  MapPin,
  Calculator
} from 'lucide-react';
import SEO from '../../components/SEO';
import { dbService } from '../../lib/supabase';
import { Bank, BankBranch } from '../../types';

const BelowTheFold = React.lazy(() => import('./BelowTheFold'));

const BelowTheFoldSkeleton = () => (
  <div className="w-full bg-slate-50/50" style={{ minHeight: '1200px' }}>
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between space-y-4 sm:space-y-0">
        <div className="text-center sm:text-left">
          <h2 className="font-sans text-xl sm:text-2xl font-bold tracking-tight text-main-text">
            Popular Banks
          </h2>
          <p className="mt-1 text-xs sm:text-sm text-slate-500">
            Browse popular banks, find branch locations, and check IFSC codes.
          </p>
        </div>
        <Link
          to="/bank"
          className="text-xs font-bold text-brand-blue hover:text-brand-blue-hover flex items-center justify-center space-x-1 border border-slate-200 hover:border-[#082C78]/20 rounded-xl bg-white px-3.5 py-2 shadow-sm transition self-center sm:self-auto"
        >
          <span>Browse All Banks</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div
            key={`skeleton-bank-${idx}`}
            className="flex items-center space-x-3 rounded-xl border border-slate-200 bg-white p-3.5 h-17"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100 p-1.5 overflow-hidden">
              <div className="h-full w-full bg-slate-200 rounded animate-pulse" />
            </div>
            <div className="grow min-w-0 space-y-1.5">
              <div className="h-4 bg-slate-100 rounded w-3/4 animate-pulse" />
              <div className="h-3 bg-slate-50 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

const POPULAR_BANK_SLUGS = [
  'state-bank-of-india',
  'hdfc-bank',
  'icici-bank-limited',
  'punjab-national-bank',
  'canara-bank',
  'bank-of-baroda',
  'axis-bank',
  'union-bank-of-india'
];

export default function Home() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<(BankBranch & { bankName: string })[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [popularBanks, setPopularBanks] = useState<Bank[]>([]);
  const [, setStats] = useState({ banks: 0, branches: 0 });
  const [shouldRenderBelow, setShouldRenderBelow] = useState(false);
  const [canRenderBelow, setCanRenderBelow] = useState(false);

  // Load popular banks and stats in parallel using Promise.all after first paint
  useEffect(() => {
    async function loadStatsAndData() {
      try {
        const [popularBanksData, branchCount, totalBanksCount] = await Promise.all([
          dbService.getPopularBanksBySlugs(POPULAR_BANK_SLUGS),
          dbService.getTotalBranchCount(),
          dbService.getTotalBankCount()
        ]);

        // Sort the returned popular banks by popular Indian banks distribution to guarantee SBI, HDFC, ICICI, etc. are listed first
        const sortedBanks = [...popularBanksData].sort((a, b) => {
          const aIdx = POPULAR_BANK_SLUGS.indexOf(a.slug);
          const bIdx = POPULAR_BANK_SLUGS.indexOf(b.slug);
          if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
          if (aIdx !== -1) return -1;
          if (bIdx !== -1) return 1;
          return a.name.localeCompare(b.name);
        });

        setPopularBanks(sortedBanks.slice(0, 6));
        setStats({
          banks: totalBanksCount,
          branches: branchCount
        });
        setShouldRenderBelow(true);
      } catch (err) {
        console.error('Concurrent homepage payload retrieval error:', err);
      }
    }

    let animationFrameId: number | undefined;
    let timeoutId: any;

    if (typeof window !== 'undefined') {
      // Use requestAnimationFrame to align mounting with the browser's render cycle,
      // preventing GPU/compositing tearing artifacts on Android Chrome.
      animationFrameId = requestAnimationFrame(() => {
        setCanRenderBelow(true);
        
        // Fetch data after the component is mounted to prioritize visual readiness
        timeoutId = setTimeout(() => {
          loadStatsAndData();
        }, 50);
      });
    }

    return () => {
      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Update suggestions based on query
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    // IFSC Shortcut: Detect valid IFSC pattern and navigate directly
    const upperQ = q.toUpperCase();
    if (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(upperQ)) {
      navigate(`/ifsc/${upperQ}`);
      return;
    }

    setIsSearching(true);
    const handler = setTimeout(async () => {
      try {
        const matches = await dbService.searchBranches(q);
        setSuggestions(matches.slice(0, 5));
      } catch (err) {
        console.error('Failed to load suggestions:', err);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, navigate]);

  // Handle smooth scroll to hash segments (like #guides)
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#guides') {
        const el = document.getElementById('guides-section');
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth' });
          }, 150);
        }
      }
    };
    
    handleHashChange();
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      const upperQ = q.toUpperCase();
      if (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(upperQ)) {
        navigate(`/ifsc/${upperQ}`);
      } else {
        navigate(`/search?q=${encodeURIComponent(q)}`);
      }
    }
  };

  return (
    <div id="home-view" className="relative min-h-screen">
      <SEO
        title="IFSC Code Finder, Bank Branch Directory & Financial Calculators | Finsahaj"
        description="Find IFSC codes, bank branches, addresses, phone numbers and use free EMI, FD, RD and SIP calculators with easy banking guides on Finsahaj."
        canonicalUrl="https://finsahaj.com/"
        ogType="website"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${window.location.origin}/#organization`,
              "name": "Finsahaj",
              "url": window.location.origin,
              "logo": {
                "@type": "ImageObject",
                "url": `${window.location.origin}/assets/branding/finsahaj-logo.webp`
              }
            },
            {
              "@type": "WebSite",
              "@id": `${window.location.origin}/#website`,
              "url": window.location.origin,
              "name": "Finsahaj",
              "publisher": {
                "@id": `${window.location.origin}/#organization`
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": `${window.location.origin}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@type": "FinancialService",
              "name": "Finsahaj",
              "description": "Finsahaj is an independent banking and financial information guide.",
              "url": window.location.origin,
              "logo": `${window.location.origin}/assets/branding/finsahaj-logo.webp`
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "How do I find my bank branch's IFSC code?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You can find your branch's IFSC code using our search tool at the top of this page. Alternatively, it is printed on your bank passbook, check leaves, or can be found by navigating our state and city-wise bank directories."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the difference between NEFT, RTGS, and IMPS?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "NEFT is a batch-based electronic fund transfer option with no maximum limits, RTGS is a real-time settlement system designed for high-value transfers above ₹2 Lakhs, and IMPS is an instant, round-the-clock transfer option for immediate peer-to-peer transfers."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are the interest calculations on Finsahaj official or guaranteed?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "All estimators, maturity values, and interest projections on Finsahaj are for educational illustration purposes. Standard rates fluctuate per individual bank schedules and RBI benchmark movements. Always verify with your respective financial institution before committing to a plan."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Finsahaj owned or endorsed by the Reserve Bank of India (RBI)?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. Finsahaj is an independent banking and financial information guide. We compile publicly available records and official directories to simplify branch discovery and calculation tools for consumers, with no affiliation to any regulatory body."
                  }
                }
              ]
            }
          ]
        }}
      />

      {/* SECTION 1: HERO & LARGE INTERACTIVE SEARCH */}
      <section className="relative overflow-hidden bg-linear-to-br from-[#082C78] via-[#0b2251] to-slate-950 py-10 text-white sm:py-12">
        {/* Abstract background vector overlays - optimized with GPU-friendly linear gradient */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,184,176,0.15)_0%,rgba(0,0,0,0)_100%)] pointer-events-none" />
        
        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="flex justify-center mb-3">
            <span className="inline-flex items-center space-x-1 border border-[#10B8B0]/20 rounded-full bg-[#10B8B0]/10 px-3 py-1 text-xs font-semibold text-[#10B8B0]">
              <Shield className="h-3.5 w-3.5 text-[#10B8B0]" strokeWidth={1.8} />
              <span>India's Banking Information Hub</span>
            </span>
          </div>

          <h1 className="font-sans text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Find IFSC Codes, Bank Branches & <span className="text-[#10B8B0]">Financial Tools</span>
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base text-slate-200 leading-relaxed font-sans">
            Find IFSC codes, bank branch details and banking information across India. Use free EMI, FD, SIP and loan calculators, compare banking products, and explore easy-to-understand banking guides.
          </p>

          {/* Core Autocomplete Search Container */}
          <div className="mx-auto mt-6 max-w-2xl relative">
            <form onSubmit={handleSearchSubmit} className="relative flex items-center">
              <div className="relative w-full">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-405">
                  <Search className="h-5 w-5" />
                </div>
                <input
                  id="main-hero-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by IFSC Code, Bank Name, Branch or City"
                  className="block w-full rounded-2xl border-0 bg-white py-4.5 pl-12 pr-28 text-main-text shadow-xl ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-4 focus:ring-[#082C78]/30 text-sm sm:text-base"
                  autoComplete="off"
                />
              </div>
              <button
                id="main-hero-search-submit"
                type="submit"
                className="absolute right-2 rounded-xl bg-[#082C78] px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-[#06215a] transition-all cursor-pointer"
              >
                Search
              </button>
            </form>

            {/* Suggestions Overlay Dropdown */}
            {searchQuery.trim().length >= 2 && (
              <div
                id="search-suggestions-container"
                className="absolute left-0 right-0 z-50 mt-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl text-left overflow-hidden"
              >
                {suggestions.length > 0 ? (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Suggested Matches
                    </div>
                    <div className="divide-y divide-slate-50">
                      {suggestions.map((sug) => (
                        <button
                          key={sug.ifsc}
                          onClick={() => navigate(`/ifsc/${sug.ifsc}`)}
                          className="flex w-full items-start space-x-3 rounded-lg p-3 text-left hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          <Building className="mt-1 h-5 w-5 text-brand-blue shrink-0" strokeWidth={1.8} />
                          <div className="grow">
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-sm text-main-text">
                              {sug.bankName ||
                               sug.bank_name ||
                               sug.banks?.name ||
                               'Unknown Bank'}</span>
                              <span className="font-mono text-xs font-bold text-brand-blue bg-blue-surface border border-brand-blue/20 px-1.5 py-0.5 rounded-xl">
                                {sug.ifsc}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {sug.branch_name} | {sug.city1 || sug.display_city}, {sug.state}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-slate-100 p-2 text-center">
                      <button
                        onClick={() => navigate(`/search?q=${encodeURIComponent(searchQuery)}`)}
                        className="text-xs text-brand-blue hover:text-[#06215a] font-semibold flex items-center justify-center space-x-1 mx-auto cursor-pointer"
                      >
                        <span>View all search results</span>
                        <ArrowRight className="h-3 w-3" strokeWidth={1.8} />
                      </button>
                    </div>
                  </>
                ) : !isSearching ? (
                  <div className="p-6 text-center">
                    <p className="text-sm text-main-text font-bold mb-1">No matching branches found</p>
                    <p className="text-xs text-slate-500 mb-4">We couldn't find any results for "{searchQuery}".</p>
                    <div className="text-xs text-slate-400 mb-2 font-semibold uppercase tracking-wider">Try searching by</div>
                    <div className="flex flex-wrap justify-center gap-2 text-xs">
                      <button onClick={() => setSearchQuery('SBIN0000164')} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">IFSC Code</button>
                      <button onClick={() => setSearchQuery('HDFC')} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">Bank Name</button>
                      <button onClick={() => setSearchQuery('Mumbai')} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">City</button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#082C78] border-t-transparent mb-3"></div>
                    <p className="text-xs text-slate-500">Searching...</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-slate-400">
            <span>Try searching:</span>
            <button onClick={() => setSearchQuery('SBIN0000164')} className="underline hover:text-white transition-colors">SBIN0000164</button>
            <span>•</span>
            <button onClick={() => setSearchQuery('Ranchi')} className="underline hover:text-white transition-colors">Ranchi</button>
            <span>•</span>
            <button onClick={() => setSearchQuery('HDFC')} className="underline hover:text-white transition-colors">HDFC Bank</button>
            <span>•</span>
            <button onClick={() => setSearchQuery('Rampur')} className="underline hover:text-white transition-colors">Rampur</button>
          </div>
        </div>
      </section>

      {/* TRUST STRIP SECTION */}
      <div className="bg-[#EEFAF9] border-b border-[#10B8B0]/20 py-5 shadow-xs relative z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-y-4 gap-x-2 md:grid-cols-4 md:gap-x-4 md:divide-x md:divide-[#10B8B0]/15">
            <div className="flex items-center space-x-2.5 justify-center md:justify-start">
              <div className="rounded-xl bg-[#10B8B0]/10 p-2 text-[#10B8B0] shrink-0 border border-[#10B8B0]/15">
                <ShieldCheck className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-800 leading-snug truncate">Verified Information</p>
                <p className="text-[9px] text-slate-500 font-medium leading-none mt-0.5">Sourced from official records</p>
              </div>
            </div>

            <div className="flex items-center space-x-2.5 justify-center md:pl-4">
              <div className="rounded-xl bg-[#10B8B0]/10 p-2 text-[#10B8B0] shrink-0 border border-[#10B8B0]/15">
                <Clock className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-800 leading-snug truncate">Regularly Updated</p>
                <p className="text-[9px] text-slate-500 font-medium leading-none mt-0.5">Latest branch details</p>
              </div>
            </div>

            <div className="flex items-center space-x-2.5 justify-center md:pl-4">
              <div className="rounded-xl bg-[#10B8B0]/10 p-2 text-[#10B8B0] shrink-0 border border-[#10B8B0]/15">
                <MapPin className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-800 leading-snug truncate">Fast IFSC Search</p>
                <p className="text-[9px] text-slate-500 font-medium leading-none mt-0.5">Quick and accurate results</p>
              </div>
            </div>

            <div className="flex items-center space-x-2.5 justify-center md:pl-4">
              <div className="rounded-xl bg-[#10B8B0]/10 p-2 text-[#10B8B0] shrink-0 border border-[#10B8B0]/15">
                <Calculator className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-800 leading-snug truncate">Free Financial Tools</p>
                <p className="text-[9px] text-slate-500 font-medium leading-none mt-0.5">Calculators & educational guides</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: POPULAR BANKS & OTHER SECTIONS */}
      {canRenderBelow ? (
        <React.Suspense fallback={<BelowTheFoldSkeleton />}>
          <BelowTheFold 
            popularBanks={popularBanks} 
            isLoading={!shouldRenderBelow} 
          />
        </React.Suspense>
      ) : (
        <BelowTheFoldSkeleton />
      )}
    </div>
  );
}
